// One Socket.IO connection per tab, owned by the app rather than by a page.
//
// Presence is derived from having a live socket: config/socket.js marks the user
// online on their first socket and offline when the last one goes. The socket
// used to be created in MessageSection.vue's mounted() and disconnected in its
// beforeUnmount(), so a signed-in user counted as online only while the Messages
// page was open — open the dashboard, or any other page, and everyone else saw
// them grey within the second.
//
// So the connection is opened as soon as a token exists, on whatever page the
// user lands on, and closed only when the session ends. MessageSection borrows
// this socket for its message events instead of owning one.

import { io } from "socket.io-client";
import { API_ORIGIN } from "@/config/apiBase";

let socket = null;
let connectedToken = null;

/**
 * Open the connection if it isn't already open. Safe and cheap to call on every
 * route change and after every login — it returns the existing socket unless the
 * token changed (a fresh sign-in), in which case the old one is replaced so the
 * handshake carries the new JWT.
 */
export function connectSocket() {
  const token = localStorage.getItem("auth-token");
  if (!token) return null;

  if (socket) {
    if (connectedToken === token) return socket;
    disconnectSocket();
  }

  // API_ORIGIN is "" on the web (same-origin), so this only redirects the
  // handshake inside the native Capacitor app, where window.location.origin
  // is capacitor://localhost and useless as a backend address.
  socket = io(API_ORIGIN || window.location.origin, {
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionDelay: 1000,
    // This socket lives for the whole session, so it must survive a laptop
    // waking up or a wifi hop. The old page-scoped socket gave up after 5
    // attempts; here that would leave the user silently offline (and missing
    // real-time messages) until they reloaded.
    reconnectionDelayMax: 10000,
    reconnectionAttempts: Infinity,
    auth: { token },
  });
  connectedToken = token;

  return socket;
}

/** The current socket, or null when signed out. */
export function getSocket() {
  return socket;
}

/**
 * End the connection. Called on sign-out and on session expiry — the server
 * authenticated the handshake when the token was still valid and keeps the
 * socket open regardless of the token expiring, so without this a signed-out
 * user would keep showing as online.
 */
export function disconnectSocket() {
  if (!socket) return;
  socket.removeAllListeners();
  socket.disconnect();
  socket = null;
  connectedToken = null;
}
